/**
 * The Meta ads integration, from the browser's side.
 *
 * Every shape here is `@wend/shared`'s — nothing about an ad account is
 * described in this app. What the file adds is the three requests and one
 * envelope the API composes rather than declares, which is the same division
 * `lib/api/whatsapp.ts` keeps.
 *
 * ## THERE IS NO TOKEN ON ANY SHAPE IN THIS FILE, IN EITHER DIRECTION
 *
 * `connectMetaAdsRequestSchema` carries one on the way *in* and nothing carries
 * one back: `metaAdsIntegrationSchema` has no field it could travel in, and it
 * is `.strict()`, so a server that tried would fail this app's parse of the
 * response. That is the guarantee, checked on every request rather than
 * remembered.
 *
 * ## WHY `grant` IS TAKEN OFF THE WIRE AND NOT DERIVED HERE
 *
 * The browser has `lastCheck.token.scopes` and could compute the answer itself.
 * It must not, for the reason `readiness` is not derived here either: the
 * **server** decides whether a write may go to Meta, slices 42 and 43 ask the
 * same `metaAdsGrant` before sending anything, and a browser that works the
 * verdict out for itself is a browser that can disagree with the guard. One
 * derivation, server-side, rendered here.
 */
import { z } from 'zod';

import { apiRequest } from './client';
import { fetchIntegrationRows } from './integrations';

/*
 * Re-exported so the screens keep importing the ads vocabulary from one place,
 * exactly as `lib/api/whatsapp.ts` re-exports the WhatsApp half.
 */
export {
  connectMetaAdsRequestSchema,
  metaAdAccountCanSpend,
  metaAdAccountSchema,
  metaAdAccountStatusSchema,
  metaAdsCheckFailureSchema,
  metaAdsCheckOutcomeSchema,
  metaAdsCheckResultSchema,
  metaAdsGrantSchema,
  metaAdsIntegrationSchema,
  metaAdsTransportSchema,
  metaTokenExpiry,
  normaliseAdAccountId,
  META_ADS_FAILURE_CODE_VALUES,
  META_ADS_GRANT_UNKNOWN,
  META_ADS_SCOPE_MANAGE,
  META_ADS_SCOPE_READ,
  META_AD_ACCOUNT_STATUS_VALUES,
  type ConnectMetaAdsRequest,
  type MetaAdAccount,
  type MetaAdAccountStatus,
  type MetaAdsCheckFailure,
  type MetaAdsCheckOutcome,
  type MetaAdsCheckResult,
  type MetaAdsFailureCode,
  type MetaAdsGrant,
  type MetaAdsIntegration,
  type MetaAdsTransport,
} from '@wend/shared';

import {
  connectMetaAdsRequestSchema as connectSchema,
  metaAdControlHistorySchema as controlHistorySchema,
  metaAdControlResultSchema as controlResultSchema,
  metaAdDeliveryStateSchema as deliveryStateSchema,
  metaAdResultsStateSchema as resultsStateSchema,
  metaAdsPeriodStateSchema as periodStateSchema,
  metaAdsCheckOutcomeSchema as outcomeSchema,
  metaAdsGrantSchema as grantSchema,
  metaAdsIntegrationSchema as integrationSchema,
  metaAdDraftListSchema as draftListSchema,
  metaAdDraftRecordSchema as draftRecordSchema,
  metaAdsReadStateSchema as readStateSchema,
  metaBudgetChangeRequestSchema as budgetChangeSchema,
  metaSpendCapRequestSchema as spendCapSchema,
  metaStartRequestSchema as startSchema,
  type MetaAdControlHistory as ControlHistory,
  type MetaAdControlResult as ControlResult,
  type MetaAdDeliveryState as DeliveryState,
  type MetaAdResultsState as ResultsState,
  type MetaAdsPeriodQuery as PeriodQuery,
  type MetaAdsPeriodState as PeriodState,
  type MetaAdDraftList as DraftList,
  type MetaAdDraftListQuery as DraftListQuery,
  type MetaAdDraftRecord as DraftRecord,
  type MetaAdDraftRequest as DraftRequest,
  type MetaBudgetChangeRequest as BudgetChangeRequest,
  type MetaSpendCapRequest as SpendCapRequest,
  type MetaStartRequest as StartRequest,
  okResponseSchema,
  type ConnectMetaAdsRequest as ConnectRequest,
  type MetaAdsReadState as ReadState,
  type MetaAdsIntegration as Integration,
  type OkResponse,
} from '@wend/shared';

/**
 * What a connect and a Test answer with.
 *
 * Three parts. `integration` is the persisted row the card renders — its
 * `grant` is what Meta **last confirmed**, which after a failed Test is the
 * previous check and is correctly still the last thing known. `outcome` carries
 * what the row cannot: which transport answered, so a green from the stub can
 * never be mistaken for a green from Meta, and the failure *code*, which is
 * what sends somebody to App Review rather than to Business Settings. `grant`
 * is what **this call** learned, and is the unknown grant when the check
 * failed.
 *
 * The two grants differing is the point, not a redundancy: a panel showing the
 * result of a Test wants the second, a panel showing the state of the
 * connection wants the first, and collapsing them makes one of those readings
 * wrong.
 */
export const metaAdsCheckResponseSchema = z
  .object({
    integration: integrationSchema,
    outcome: outcomeSchema,
    grant: grantSchema,
  })
  .strict();
export type MetaAdsCheckResponse = z.infer<typeof metaAdsCheckResponseSchema>;

/* -------------------------------------------------------------------------- */
/* The integration                                                             */
/* -------------------------------------------------------------------------- */

/**
 * The ads integration, or `null` when this workspace has not connected one.
 *
 * Read off the list route rather than `GET /integrations/meta-ads`, for the
 * reason the SES and WhatsApp readers are: the list answers `[]` for a
 * workspace that has connected nothing, while the fetch-one route answers 404 —
 * and "not connected yet" is the ordinary starting state of this card, not an
 * error to catch and discard.
 */
export async function fetchMetaAdsIntegration(signal?: AbortSignal): Promise<Integration | null> {
  const rows = await fetchIntegrationRows(signal);
  const row = rows.find((candidate) => candidate.kind === 'meta_ads');
  return row === undefined ? null : integrationSchema.parse(row);
}

/**
 * Connects the tenant's own ad account, or replaces the one already connected,
 * and checks it in the same round trip.
 *
 * `PUT`, because it is idempotent and replaces wholesale — `UNIQUE (tenant_id,
 * kind)` — and because there is no partial update: changing the ad account
 * means sending the token again, since a half-updated credential fails when
 * somebody presses pause rather than here at the form.
 *
 * A check that fails is still a 200. The credentials were stored, the row says
 * `failed`, and `outcome.failure.message` says what to repair — so this rejects
 * only when the *request* failed, never when Meta did.
 */
export async function connectMetaAds(request: ConnectRequest): Promise<MetaAdsCheckResponse> {
  return apiRequest({
    path: '/integrations/meta-ads',
    method: 'PUT',
    body: connectSchema.parse(request),
    schema: metaAdsCheckResponseSchema,
  });
}

/** Re-runs the check against the stored token. The Test button. */
export async function checkMetaAds(): Promise<MetaAdsCheckResponse> {
  return apiRequest({
    path: '/integrations/meta-ads/check',
    method: 'POST',
    schema: metaAdsCheckResponseSchema,
  });
}

/** Disconnects: the row goes, and the sealed token goes with it. */
export async function disconnectMetaAds(): Promise<OkResponse> {
  return apiRequest({
    /* `okResponseSchema`, not a hand-written `{ ok: true }`: every OK envelope
       in this API is `{ success: true }`, and a locally invented shape would
       make a successful disconnect look to the card like a malformed
       response. */
    path: '/integrations/meta-ads',
    method: 'DELETE',
    schema: okResponseSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Reading the account — slice 41                                             */
/* -------------------------------------------------------------------------- */

/**
 * The connected ad account over the last `days` days, in the account's own
 * reporting zone.
 *
 * Always a state, never a bare list — see `metaAdsReadStateSchema`. A workspace
 * whose token cannot read gets `not_permitted` or `not_checked`, and there is
 * no `campaigns: []` on either for a screen to draw as an empty account.
 *
 * A read Meta refused is still a 200 with `state: 'failed'`: this rejects only
 * when the *request* failed, never when Meta did.
 */
export async function fetchMetaAdsAccountRead(days: number, signal?: AbortSignal): Promise<ReadState> {
  return apiRequest({
    path: '/meta-ads/account',
    query: { days: String(days) },
    schema: readStateSchema,
    signal,
  });
}

/* -------------------------------------------------------------------------- */
/* Drafts — slice 42                                                           */
/* -------------------------------------------------------------------------- */

/**
 * One page of this workspace's drafts, newest change first — slice 52, gap 4.
 *
 * The search and the surface filter are the **server's** since this slice. The
 * panel used to take the newest hundred and filter them in the browser, so
 * "No match" could mean "not among the newest hundred" and a search past them
 * found nothing while a matching draft sat in the database. Both now run in
 * SQL over the whole of the tenant's drafts, and a page of 25 is 25 matching
 * rows rather than 25 rows of which 3 match.
 *
 * `cursor` is the opaque `(updatedAt, id)` pair the previous page answered
 * with; it is sent back exactly as received and never built here. A cursor the
 * API cannot parse is a 400, which the panel draws on its "Show more" button.
 * Nothing in this route reaches Meta, so it spends none of the Meta read limit.
 */
export async function listMetaAdDrafts(query: DraftListQuery, signal?: AbortSignal): Promise<DraftList> {
  return apiRequest({
    path: '/meta-ads/drafts',
    query: {
      limit: String(query.limit),
      ...(query.q === undefined || query.q === '' ? {} : { q: query.q }),
      ...(query.surface === undefined ? {} : { surface: query.surface }),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
    },
    schema: draftListSchema,
    signal,
  });
}

export async function fetchMetaAdDraft(id: string, signal?: AbortSignal): Promise<DraftRecord> {
  return apiRequest({ path: `/meta-ads/drafts/${encodeURIComponent(id)}`, schema: draftRecordSchema, signal });
}

/** Composes a new draft. Nothing reaches Meta. */
export async function createMetaAdDraft(request: DraftRequest): Promise<DraftRecord> {
  return apiRequest({ path: '/meta-ads/drafts', method: 'POST', body: request, schema: draftRecordSchema });
}

/**
 * Replaces a draft's content. `version` is the one the person was editing — a
 * save over somebody else's edit comes back as a conflict, not an overwrite.
 */
export async function updateMetaAdDraft(id: string, version: number, request: DraftRequest): Promise<DraftRecord> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}`,
    method: 'PUT',
    body: { version, draft: request },
    schema: draftRecordSchema,
  });
}

export async function discardMetaAdDraft(id: string): Promise<OkResponse> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}`,
    method: 'DELETE',
    schema: okResponseSchema,
  });
}

/**
 * Publishes the version the person reviewed. Everything is created at Meta
 * PAUSED; a publish that fails part way comes back as a `failed` record with
 * what it created recorded, not as a thrown error.
 */
export async function publishMetaAdDraft(id: string, version: number): Promise<DraftRecord> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/publish`,
    method: 'POST',
    body: { version },
    schema: draftRecordSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* Controls — slice 43                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A published draft's campaign as Meta has it now. Always a state: a refusal
 * decided from the rows, or Meta's own failure, is a 200 like the account read.
 */
export async function fetchMetaAdDelivery(id: string, signal?: AbortSignal): Promise<DeliveryState> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/delivery`,
    schema: deliveryStateSchema,
    signal,
  });
}

/** Every change Wend sent to Meta for this draft, newest first. */
export async function fetchMetaAdHistory(id: string, signal?: AbortSignal): Promise<ControlHistory> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/history`,
    schema: controlHistorySchema,
    signal,
  });
}

/*
 * The four changes. Each rejects when Wend refused before sending anything —
 * the refusal is the error's message — and resolves once a change was sent,
 * `done` or `stopped`: a stopped change is a result to draw, not an error.
 */

export async function startMetaAd(id: string, request: StartRequest): Promise<ControlResult> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/start`,
    method: 'POST',
    body: startSchema.parse(request),
    schema: controlResultSchema,
  });
}

export async function pauseMetaAd(id: string): Promise<ControlResult> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/pause`,
    method: 'POST',
    schema: controlResultSchema,
  });
}

export async function changeMetaAdBudget(id: string, request: BudgetChangeRequest): Promise<ControlResult> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/budget`,
    method: 'POST',
    body: budgetChangeSchema.parse(request),
    schema: controlResultSchema,
  });
}

export async function changeMetaAdSpendCap(id: string, request: SpendCapRequest): Promise<ControlResult> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/spend-cap`,
    method: 'POST',
    body: spendCapSchema.parse(request),
    schema: controlResultSchema,
  });
}

/* -------------------------------------------------------------------------- */
/* The ad surfaces — slice 44                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A published draft's campaign over its lifetime, the whole and by placement
 * — its per-surface panels. The delivery read's states, as a 200; a draft
 * this workspace does not have is a 404, and the Meta read limit a 429, both
 * thrown as the request's failure.
 */
export async function fetchMetaAdResults(id: string, signal?: AbortSignal): Promise<ResultsState> {
  return apiRequest({
    path: `/meta-ads/drafts/${encodeURIComponent(id)}/results`,
    schema: resultsStateSchema,
    signal,
  });
}

/**
 * The ad account over two picked days, both included — read by the API in
 * the account's own zone, which the answer names. Analytics' ad surfaces.
 */
export async function fetchMetaAdsPeriod(query: PeriodQuery, signal?: AbortSignal): Promise<PeriodState> {
  return apiRequest({
    path: '/meta-ads/period',
    query: { fromDate: query.fromDate, toDate: query.toDate },
    schema: periodStateSchema,
    signal,
  });
}
